'use client';

// module imports
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// file imports
import { DeleteConfirmationModal } from '@/components';
import { deleteLead, getLeads, updateLead } from '@/redux/slices/leadSlice';
import type { AppDispatch } from '@/redux/store';

interface LeadActionsProps {
  id: string;
  isOpen: boolean;
  onToggle: () => void;
  currentTags?: string[];
  currentPage: number;
}

export default function LeadActions({ id, isOpen, onToggle, currentTags = [], currentPage }: LeadActionsProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'right' | 'left'>('right');
  const [verticalPosition, setVerticalPosition] = useState<'below' | 'above'>('below');
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await dispatch(deleteLead(id));
      dispatch(getLeads({ params: { limit: 10, page: currentPage, sort: -1 } }));
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Failed to delete lead:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleAddTagClick = () => {
    setShowTagInput(true);
    setPendingTags([...currentTags]); // Initialize with current tags
    setNewTag('');
  };

  const addTagToPending = () => {
    const tagToAdd = newTag.trim();
    if (!tagToAdd || pendingTags.includes(tagToAdd)) {
      setNewTag('');
      return;
    }
    setPendingTags([...pendingTags, tagToAdd]);
    setNewTag('');
  };

  const removePendingTag = (tagToRemove: string) => {
    setPendingTags(pendingTags.filter((tag) => tag !== tagToRemove));
  };

  const handleSaveTags = async () => {
    setIsUpdating(true);
    try {
      await dispatch(updateLead({ id, updatedData: { tags: pendingTags } }));
      dispatch(getLeads({ params: { limit: 10, page: currentPage, sort: -1 } }));
      setShowTagInput(false);
      setNewTag('');
    } catch (error) {
      console.error('Failed to save tags:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelTags = () => {
    setShowTagInput(false);
    setNewTag('');
    setPendingTags([...currentTags]); // Reset to original tags
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isEnter = e.key === 'Enter';
    const isSpace = e.key === ' ';
    const isEscape = e.key === 'Escape';

    if (isEnter || isSpace) {
      e.preventDefault();
      e.stopPropagation();
      addTagToPending();
    } else if (isEscape) {
      e.preventDefault();
      e.stopPropagation();
      handleCancelTags(); // Cancel without saving
    }
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isEnter = e.key === 'Enter';
    const isSpace = e.key === ' ';

    if (isEnter || isSpace) {
      e.preventDefault();
      e.stopPropagation();
      addTagToPending();
    }
  };

  const handleTagInputClose = () => {
    handleSaveTags();
  };

  // Calculate dropdown position to prevent overflow and get button position
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      // Adjust height based on number of pending tags
      const tagsHeight = pendingTags.length > 0 ? Math.min(pendingTags.length * 28 + 60, 200) : 60;
      const dropdownHeight = showTagInput ? tagsHeight : 60;

      // Store button position for fixed positioning
      setButtonRect(rect);

      // Always position on the right side
      setDropdownPosition('right');

      // Check if dropdown would overflow at the bottom
      if (rect.bottom + dropdownHeight > viewportHeight) {
        setVerticalPosition('above');
      } else {
        setVerticalPosition('below');
      }
    } else {
      setButtonRect(null);
    }
  }, [isOpen, showTagInput, pendingTags.length]);

  // Focus input when tag input is shown
  useEffect(() => {
    if (showTagInput && tagInputRef.current) {
      tagInputRef.current.focus();
    }
  }, [showTagInput]);

  return (
    <div className="relative dropdown-container" ref={dropdownRef}>
      <button
        className="text-gray-500 hover:text-gray-800 cursor-pointer transition-colors duration-150 p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label="Lead actions menu"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {isOpen && buttonRect && (
        <div
          className="fixed w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200 dropdown-container"
          style={{
            top: verticalPosition === 'below' ? buttonRect.bottom + 8 : buttonRect.top - (showTagInput ? Math.min(Math.max(pendingTags.length * 28 + 80, 100), 280) : 128),
            left: dropdownPosition === 'right' ? buttonRect.right - 320 : buttonRect.left,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1">
            {!showTagInput ? (
              <>
                <button onClick={handleAddTagClick} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer duration-150 whitespace-nowrap">
                  <span className="mr-3 text-base">✏️</span>Add Tags
                </button>
                <button onClick={handleDeleteClick} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer transition-colors cursor-pointer duration-150 whitespace-nowrap" title="Delete Lead">
                  <span className="mr-3 text-base">🗑️</span>Delete
                </button>
              </>
            ) : (
              <div className="px-4 py-2">
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Add Tags</h3>

                  {/* Display pending tags */}
                  {pendingTags.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                      {pendingTags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          {tag}
                          <button onClick={() => removePendingTag(tag)} disabled={isUpdating} className="ml-1 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors" aria-label={`Remove tag ${tag}`}>
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 mb-2">
                    <input ref={tagInputRef} type="text" value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={handleTagInputKeyDown} onKeyPress={handleTagInputKeyPress} placeholder="Type tag and press Enter" className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" disabled={isUpdating} inputMode="text" />
                    <button onClick={addTagToPending} disabled={isUpdating || !newTag.trim()} className="px-4 py-2 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap" type="button">
                      Add
                    </button>
                  </div>
                  {isUpdating && <p className="text-xs text-gray-500 mt-1">Saving tags...</p>}
                  <p className="text-xs text-gray-400 mt-1">Press Enter to add tag or Escape to cancel</p>
                </div>

                <div className="border-t pt-2 flex gap-2">
                  <button onClick={handleTagInputClose} disabled={isUpdating} className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer duration-150">
                    {isUpdating ? 'Saving...' : 'Save Tags'}
                  </button>
                  <button onClick={handleCancelTags} disabled={isUpdating} className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer duration-150 whitespace-nowrap">
                    Cancel
                  </button>
                  <button onClick={handleDeleteClick} className="flex items-center px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 cursor-pointer transition-colors duration-150 whitespace-nowrap rounded-md">
                    <span className="mr-1 text-base">🗑️</span>Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal isOpen={showDeleteModal} onClose={handleDeleteCancel} onConfirm={handleDeleteConfirm} title="Delete Lead" message="Are you sure you want to delete this lead? This action cannot be undone." confirmText="DELETE" isDeleting={isDeleting} requireTextInput={false} itemName="lead" />
    </div>
  );
}
